import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Home, 
  BookOpen, 
  Microscope, 
  Trophy,
  Music,
  Users,
  Wifi,
  Utensils,
  Shield,
  ArrowRight
} from 'lucide-react';
import { normalizeImageUrl } from '../lib/imageUrl';
import { usePage } from '../contexts/CmsContext';

const CAMPUS_HERO = "https://images.unsplash.com/photo-1562774053-701939374585?w=1920&q=80";
const HOSTEL_IMAGE = "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=800&q=80";
const LIBRARY_IMAGE = "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80";
const LAB_IMAGE = "https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=800&q=80";
const SPORTS_IMAGE = "https://images.unsplash.com/photo-1461896836934-3c8f3d0e6e8e?w=800&q=80";

const facilities = [
  {
    icon: Home,
    title: 'Hostel Accommodation',
    description: 'Separate hostels for boys and girls with comfortable rooms, common areas, and 24/7 security.',
    image: HOSTEL_IMAGE,
    features: ['Single/Double occupancy rooms', 'Attached bathrooms', 'Common rooms with TV', '24/7 security', 'Wi-Fi connectivity'],
  },
  {
    icon: BookOpen,
    title: 'Central Library',
    description: 'Well-stocked library with extensive medical literature, journals, and digital resources.',
    image: LIBRARY_IMAGE,
    features: ['Large collection of textbooks', 'Medical journals', 'Digital library access', 'Reading halls', 'E-resources'],
  },
  {
    icon: Microscope,
    title: 'Laboratories',
    description: 'Modern laboratories equipped with advanced equipment for practical training.',
    image: LAB_IMAGE,
    features: ['Anatomy dissection hall', 'Histology lab', 'Biochemistry lab', 'Microbiology lab', 'Clinical skills lab'],
  },
  {
    icon: Trophy,
    title: 'Sports & Recreation',
    description: 'Sports facilities and recreational areas for physical fitness and leisure activities.',
    image: SPORTS_IMAGE,
    features: ['Indoor games', 'Outdoor sports ground', 'Gymnasium', 'Yoga room', 'Walking tracks'],
  },
];

const studentActivities = [
  {
    icon: Music,
    title: 'Cultural Events',
    description: 'Annual cultural festivals, talent shows, and celebrations bringing the campus community together.',
  },
  {
    icon: Users,
    title: 'Student Clubs',
    description: 'Various clubs for academic, cultural, and extracurricular activities fostering leadership.',
  },
  {
    icon: Trophy,
    title: 'Sports Competitions',
    description: 'Inter-college tournaments and sports meets promoting teamwork and physical fitness.',
  },
];

const amenities = [
  { icon: Wifi, name: 'Wi-Fi Campus' },
  { icon: Utensils, name: 'Mess & Canteen' },
  { icon: Shield, name: '24/7 Security' },
  { icon: Home, name: 'Laundry Service' },
];

const CampusLife = () => {
  const { data: homeCms } = usePage('home');
  const cmsImages = homeCms?.campus_life_images || [];
  // Merge CMS image URLs into the local facilities list by index (preserves icons + features)
  const mergedFacilities = facilities.map((f, i) => ({
    ...f,
    image: cmsImages[i]?.image ? normalizeImageUrl(cmsImages[i].image) : f.image,
  }));
  return (
    <div className="page-transition" data-testid="campus-life-page">
      {/* Hero Section */}
      <section 
        className="relative py-20 md:py-32 hero-overlay"
        style={{
          backgroundImage: `url(${CAMPUS_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="campus-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              Student Life
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              Campus Life at SSMCH
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Experience a vibrant campus environment that nurtures academic excellence, personal growth, and lifelong friendships.
            </p>
          </div>
        </div>
      </section>

      {/* Facilities Grid */}
      <section className="section-padding bg-white" data-testid="facilities-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Facilities
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              Campus Facilities
            </h2>
            <p className="text-gray-600 text-lg">
              Modern facilities designed to support academic pursuits and comfortable living.
            </p>
          </div>

          <div className="space-y-16">
            {mergedFacilities.map((facility, index) => (
              <div 
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`facility-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-[#4ECDC4]/10 rounded-xl flex items-center justify-center">
                      <facility.icon className="text-[#4ECDC4]" size={28} />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-[#1A202C]">
                      {facility.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {facility.description}
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {facility.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#4ECDC4] rounded-full" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img 
                    src={facility.image} 
                    alt={facility.title}
                    className="rounded-2xl shadow-xl w-full h-80 object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities Bar */}
      <section className="py-12 bg-[#1A202C]" data-testid="amenities-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {amenities.map((amenity, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mb-3">
                  <amenity.icon className="text-[#4ECDC4]" size={24} />
                </div>
                <span className="text-white font-medium">{amenity.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Student Activities */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="activities-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Student Activities
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              Beyond Academics
            </h2>
            <p className="text-gray-600 text-lg">
              A rich array of activities for holistic development and vibrant campus life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {studentActivities.map((activity, index) => (
              <Card 
                key={index}
                className="card-hover border-0 shadow-sm"
                data-testid={`activity-${index}`}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#D4A84B]/20 to-[#D4A84B]/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <activity.icon className="text-[#D4A84B]" size={28} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A202C] mb-3">
                    {activity.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {activity.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="section-padding bg-white" data-testid="gallery-preview">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block bg-[#9ACD32]/10 text-[#9ACD32] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Gallery
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              Campus Gallery
            </h2>
            <p className="text-gray-600 text-lg">
              A glimpse of life at SSMCH through our campus photos.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {mergedFacilities.map((f, index) => (
              <div 
                key={index}
                className="aspect-square rounded-xl overflow-hidden group cursor-pointer"
              >
                <img 
                  src={f.image} 
                  alt={f.title || `Campus ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/gallery">
              <Button 
                variant="outline"
                className="border-2 border-[#4ECDC4] text-[#4ECDC4] hover:bg-[#4ECDC4] hover:text-white rounded-full px-8 h-12 font-medium transition-all"
                data-testid="view-gallery-btn"
              >
                View Full Gallery
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#4ECDC4] to-[#3DBDB4]" data-testid="campus-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Be Part of Our Community?
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Join SSMCH and experience a campus life that balances academic rigor with personal growth.
          </p>
          <Link to="/admissions">
            <Button 
              className="bg-white text-[#4ECDC4] hover:bg-gray-100 rounded-full px-8 h-12 font-medium shadow-lg hover:shadow-xl transition-all"
              data-testid="campus-apply-btn"
            >
              Apply for Admission
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CampusLife;
